<template>
  <div>
    <ClinicPageHeader :title="greetingTitle" :subtitle="headerSubtitle">
      <template #actions>
        <select
          v-model="month"
          class="select"
          data-test="month"
          style="width:180px;height:40px"
          @change="onMonthChange"
        >
          <option v-for="m in months" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
        <router-link
          v-if="canEdit('agenda')"
          :to="{ name: 'ClinicSchedule', query: { novo: '1' } }"
          class="btn btn--primary btn--sm"
        >
          + Novo agendamento
        </router-link>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar o painel"
        message="Ocorreu um erro ao buscar os indicadores da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetchOverview">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="metrics4" style="margin-bottom:var(--space-4)">
          <div v-for="i in 4" :key="i" class="metric">
            <div
              class="skel-line"
              style="width:36px;height:36px;border-radius:8px;margin-bottom:10px"
            />
            <div class="skel-line" style="width:70%;margin-bottom:8px" />
            <div class="skel-line" style="width:45%;height:24px" />
          </div>
        </div>
        <div class="metrics3" style="margin-bottom:var(--space-6)">
          <div v-for="i in 3" :key="i" class="metric">
            <div
              class="skel-line"
              style="width:36px;height:36px;border-radius:8px;margin-bottom:10px"
            />
            <div class="skel-line" style="width:70%;margin-bottom:8px" />
            <div class="skel-line" style="width:45%;height:24px" />
          </div>
        </div>
        <div class="card">
          <div class="skel-line" style="width:40%;margin-bottom:20px" />
          <div class="skel-line" style="width:100%;height:120px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- KPIs principais -->
        <div class="metrics4" style="margin-bottom:var(--space-4)">
          <ClinicKpi
            label="Consultas do mês"
            :value="fmtInt(kpis.consultasMes)"
            icon-color="primary"
            :icon="icons.calendar"
          />
          <ClinicKpi
            label="Realizadas"
            :value="fmtInt(kpis.realizadas)"
            icon-color="success"
            :icon="icons.check"
            :foot="
              `${kpis.pendentes} pendentes · ${kpis.canceladas} canceladas`
            "
          />
          <ClinicKpi
            label="Pacientes ativos"
            :value="fmtInt(kpis.pacientesAtivos)"
            icon-color="accent"
            :icon="icons.users"
            :foot="`de ${fmtInt(kpis.pacientesCadastrados)} cadastrados`"
          />
          <router-link
            v-if="financeVisible"
            :to="{ name: 'ClinicFinanceDashboard' }"
            class="metric card--interactive"
            style="text-decoration:none"
          >
            <div
              class="metric-icon"
              style="background:var(--color-success-light);color:var(--color-success)"
              v-html="icons.money"
            />
            <span class="metric-label">Receita do mês</span>
            <span class="metric-value sm money--pos">
              {{ money(kpis.receitaMesCents) }}
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-primary);font-weight:var(--fw-bold)"
            >
              Ver financeiro →
            </span>
          </router-link>
        </div>

        <!-- KPIs secundários -->
        <div class="metrics3" style="margin-bottom:var(--space-6)">
          <ClinicKpi
            label="Novos pacientes"
            :value="fmtInt(kpis.novosPacientes)"
            icon-color="primary"
            :icon="icons.userPlus"
          />
          <router-link
            v-if="financeVisible"
            :to="{ name: 'ClinicFinanceTransfers' }"
            class="metric card--interactive"
            style="text-decoration:none"
          >
            <div
              class="metric-icon"
              style="background:var(--color-warning-light);color:var(--color-warning)"
              v-html="icons.clock"
            />
            <span class="metric-label">Repasses pendentes</span>
            <span class="metric-value sm">
              {{ money(kpis.repassesPendentesCents) }}
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-primary);font-weight:var(--fw-bold)"
            >
              Ver repasses →
            </span>
          </router-link>
          <router-link
            :to="{ name: 'ClinicNoReturn' }"
            class="metric card--interactive"
            style="text-decoration:none;border-color:var(--color-warning)"
          >
            <div
              class="metric-icon"
              style="background:var(--color-danger-light);color:var(--color-danger)"
              v-html="icons.alert"
            />
            <span class="metric-label">Pacientes sem retorno</span>
            <span class="metric-value sm">{{ kpis.semRetorno }}</span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-accent);font-weight:var(--fw-bold)"
            >
              Iniciar acompanhamento →
            </span>
          </router-link>
        </div>

        <!-- LINHA A: evolução + alertas -->
        <div class="dash-2col" style="margin-bottom:var(--space-4)">
          <div v-if="financeVisible" class="card" data-test="evolution">
            <div class="card-head">
              <h3>Evolução · consultas realizadas</h3>
              <span class="badge badge--neutral badge--no-dot">
                últimos 6 meses
              </span>
            </div>
            <div class="bars">
              <div
                v-for="b in evolution"
                :key="b.label"
                class="bar"
                :class="{ on: b.on }"
              >
                <i :style="{ height: `${b.value}%` }" />
                <span>{{ b.label }}</span>
              </div>
            </div>
            <template v-if="summaryStats.length">
              <hr
                style="border:none;border-top:1px solid var(--color-gray-100);margin:var(--space-5) 0"
              />
              <div class="row" style="gap:var(--space-8)">
                <div
                  v-for="s in summaryStats"
                  :key="s.label"
                  class="stack"
                  style="gap:2px"
                >
                  <span class="fieldset-title" style="margin:0">
                    {{ s.label }}
                  </span>
                  <span
                    class="money"
                    :class="s.valueClass"
                    style="font-size:var(--font-size-xl)"
                  >
                    {{ s.value }}
                  </span>
                </div>
              </div>
            </template>
          </div>

          <div class="card card--flush">
            <div class="card-head flush"><h3>Alertas operacionais</h3></div>
            <div
              class="alert-stack"
              style="padding:0 var(--space-5) var(--space-5)"
            >
              <router-link
                v-for="al in alerts"
                :key="al.title"
                :to="{ name: al.route }"
                class="alert-row is-clickable"
                :class="`is-${al.level}`"
                style="text-decoration:none"
              >
                <div class="ic" :class="`ic--${al.level}`" v-html="al.icon" />
                <div class="tx">
                  <b>{{ al.title }}</b>
                  <span>{{ al.desc }}</span>
                </div>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  style="color:var(--color-gray-300);flex:none"
                >
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </router-link>
            </div>
          </div>
        </div>

        <!-- LINHA B: agenda de hoje + novos pacientes -->
        <div class="dash-2col">
          <div v-if="canView('agenda')" class="card card--flush">
            <div class="card-head flush">
              <h3>Agenda de hoje · {{ today.length }} consultas</h3>
              <router-link :to="{ name: 'ClinicSchedule' }" class="seelink">
                Ver agenda completa →
              </router-link>
            </div>

            <table
              v-if="today.length"
              class="table"
              style="margin-top:var(--space-2)"
            >
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Paciente</th>
                  <th>Profissional de saúde</th>
                  <th>Status</th>
                  <th class="t-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="ap in today"
                  :key="ap.id"
                  class="tr--link"
                  @click="goPatient(ap.patientId)"
                >
                  <td class="num">
                    <strong style="color:var(--color-gray-900)">
                      {{ ap.time }}
                    </strong>
                  </td>
                  <td>
                    <div class="row" style="gap:var(--space-3)">
                      <div class="avatar avatar--sm" :class="ap.avatarClass">
                        {{ ap.initials }}
                      </div>
                      <span class="cell-strong">{{ ap.patientName }}</span>
                    </div>
                  </td>
                  <td>{{ ap.doctorName }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="`badge--${statusOf(ap.status).variant}`"
                    >
                      {{ statusOf(ap.status).label }}
                    </span>
                  </td>
                  <td class="t-right">
                    <button
                      class="btn btn--outline btn--sm"
                      @click.stop="goPatient(ap.patientId)"
                    >
                      Ver ficha
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <ClinicStateBox
              v-else
              state="empty"
              title="Nenhuma consulta hoje"
              message="Não há consultas agendadas para hoje nesta unidade."
            />
          </div>

          <div v-if="canView('pacientes')" class="card card--flush">
            <div class="card-head flush">
              <h3>Novos pacientes</h3>
              <router-link :to="{ name: 'ClinicPatients' }" class="seelink">
                Ver todos →
              </router-link>
            </div>
            <div>
              <div
                v-for="p in newPatients"
                :key="p.id"
                class="minirow"
                style="cursor:pointer"
                @click="goPatient(p.id)"
              >
                <div class="avatar avatar--sm" :class="p.avatarClass">
                  {{ p.initials }}
                </div>
                <div class="info">
                  <b>{{ p.name }}</b>
                  <span>{{ p.specialtyLabel || p.statusLabel }}</span>
                </div>
                <span class="badge badge--info badge--no-dot">Novo</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import clinicFinanceMonth from "@/mixins/clinicFinanceMonth";
import { mapGetters } from "vuex";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicKpi from "@/components/clinica/ClinicKpi.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import { ClinicDashboard as DashboardService } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";

const ICONS = {
  calendar: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" stroke-width="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" stroke-width="2"/></svg>`,
  check: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  users: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3.5" stroke="currentColor" stroke-width="2"/><path d="M2 20c0-3.5 3-5.5 7-5.5s7 2 7 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  money: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 6H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  userPlus: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3.5" stroke="currentColor" stroke-width="2"/><path d="M2 20c0-3.5 3-5.5 7-5.5s7 2 7 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M19 8v6M16 11h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  clock: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  alert: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`
};

const ALERT_ICONS = {
  confirm: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 11l3 3L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  alert: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 8v5M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/></svg>`,
  reschedule: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  money: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 6H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
};

const STATUS = {
  agendada: { label: "Agendada", variant: "info" },
  confirmada: { label: "Confirmada", variant: "success" },
  aguardando_pgto: { label: "Aguardando pgto", variant: "warning" },
  realizada: { label: "Realizada", variant: "neutral" },
  cancelada: { label: "Cancelada", variant: "danger" }
};

export default {
  name: "ClinicDashboard",
  components: { ClinicPageHeader, ClinicKpi, ClinicStateBox },
  mixins: [clinicCan, clinicFinanceMonth],
  data() {
    return {
      state: "loading",
      icons: ICONS,
      kpis: {},
      evolution: [],
      today: [],
      newPatients: [],
      alerts: []
    };
  },
  created() {
    this.fetchOverview();
  },
  methods: {
    // KPIs, receita e evolução do mês escolhido; agenda de hoje e filas
    // continuam do momento atual (a API já separa).
    onMonthChanged() {
      this.fetchOverview();
    },
    async fetchOverview() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const { data } = await DashboardService.overview({
          month: this.month,
          fail
        });
        this.kpis = data.kpis;
        this.evolution = data.evolution;
        this.today = data.today;
        this.newPatients = data.newPatients.map(p => ({
          ...p,
          specialtyLabel: `${p.statusLabel} · ${p.specialty || ""}`
        }));
        this.alerts = this.buildAlerts(data);
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    statusOf(status) {
      return STATUS[status] || { label: status, variant: "neutral" };
    },
    fmtInt(n) {
      return (n || 0).toLocaleString("pt-BR");
    },
    money(cents) {
      return formatMoney(cents);
    },
    buildAlerts(data) {
      const alerts = [
        {
          level: "warning",
          icon: ALERT_ICONS.confirm,
          title: `${data.pendingConfirmations} consultas aguardando confirmação`,
          desc: "Confirme com os pacientes",
          route: "ClinicConfirmations"
        },
        {
          level: "danger",
          icon: ALERT_ICONS.alert,
          title: `${data.kpis.semRetorno} pacientes sem retorno`,
          desc: "Elegíveis para campanha de reativação",
          route: "ClinicNoReturn"
        },
        {
          level: "info",
          icon: ALERT_ICONS.reschedule,
          title: `${data.pendingReschedules} reagendamentos na fila`,
          desc: "Pacientes aguardando novo horário",
          route: "ClinicReschedules"
        },
        {
          level: "info",
          icon: ALERT_ICONS.money,
          title: "Repasses pendentes de distribuição",
          desc: `${this.money(data.kpis.repassesPendentesCents)} a distribuir`,
          route: "ClinicFinanceTransfers"
        }
      ];
      // Sem acesso ao Financeiro a API manda os valores nulos (US-C11).
      return data.kpis.repassesPendentesCents == null
        ? alerts.filter(a => a.route !== "ClinicFinanceTransfers")
        : alerts;
    },
    goPatient(id) {
      this.$router.push({
        name: "ClinicPatientProfile",
        params: { id: String(id) }
      });
    }
  },
  computed: {
    /** A API só manda receita/repasses a quem tem acesso ao Financeiro. */
    financeVisible() {
      return this.kpis.receitaMesCents != null;
    },
    ...mapGetters("clinica", ["operator", "currentUnit"]),
    // Saudação com o nome real do operador logado (sessão), não fixo.
    greetingTitle() {
      const h = new Date().getHours();
      const period = h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
      const first =
        this.operator && this.operator.name
          ? this.operator.name.split(/\s+/)[0]
          : "";
      return first ? `${period}, ${first}` : period;
    },
    // Data de hoje + clínica/unidade reais do contexto de sessão.
    headerSubtitle() {
      const today = new Date().toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      const parts = [today];
      // Clínica ativa (US-C11); sem ela, a do operador.
      const active = this.$store.getters["clinica/activeClinic"];
      const clinic =
        (active && active.name) || (this.operator && this.operator.clinicName);
      const unit = this.currentUnit && this.currentUnit.name;
      if (clinic) parts.push(unit ? `${clinic} — ${unit}` : clinic);
      return parts.join(" · ");
    },
    // Derivado dos KPIs reais do backend (não hardcoded). Só inclui o stat
    // quando há denominador — sem inventar valor.
    summaryStats() {
      const k = this.kpis || {};
      const realizadas = k.realizadas || 0;
      const canceladas = k.canceladas || 0;
      const resolvidas = realizadas + canceladas;
      const stats = [];
      if (realizadas > 0 && k.receitaMesCents != null) {
        stats.push({
          label: "Ticket médio",
          value: formatMoney(Math.round(k.receitaMesCents / realizadas))
        });
      }
      if (resolvidas > 0) {
        stats.push({
          label: "Taxa de comparecimento",
          value: `${Math.round((realizadas / resolvidas) * 100)}%`,
          valueClass: "money--pos"
        });
      }
      return stats;
    }
  }
};
</script>

<style lang="scss" scoped>
.dash-2col {
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: var(--space-4);
}
@media (max-width: 1100px) {
  .dash-2col {
    grid-template-columns: 1fr;
  }
}
</style>
